/**
 * TransferActivity — per-league roster-move feed for the Stage tab.
 *
 * 2026-09-03 (owner request) — "dropped Trump → added Lamar" style rows so a
 * league feels alive beyond chat. Post-finalize moves only (backend filters).
 * Sits at the bottom of Stage, directly above the League Chat button.
 * "See all" opens LeagueActivityPage (full history, paged).
 * Mobile mirror: mobile/src/components/TransferActivity.js
 */
import { useCallback, useEffect, useState, memo } from 'react';
import { ArrowLeftRight, ChevronRight, Plus, Minus, Repeat } from 'lucide-react';
import { leagueAPI } from '../lib/api';
import { buildTeamRowAvatar } from '../lib/avatars';
import { useAuth } from '../contexts/AuthContext';
import { Skeleton } from './ui/skeleton';

const POLL_MS = 60_000;
const LIMIT = 10;

export function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 30 ? `${d}d ago` : `${Math.floor(d / 30)}mo ago`;
}

const KIND = {
  swap: { Icon: Repeat, cls: 'text-[hsl(var(--accent))]' },
  add: { Icon: Plus, cls: 'text-[hsl(var(--positive-text))]' },
  drop: { Icon: Minus, cls: 'text-destructive' },
};

function Person({ p, onClick }) {
  if (!p) return null;
  if (!(onClick && p.entity_id)) return <span className="font-semibold text-foreground">{p.name}</span>;
  return (
    <button
      type="button"
      className="font-semibold text-foreground hover:underline"
      onClick={(e) => { e.stopPropagation(); onClick(p.entity_id); }}
      data-testid={`transfer-activity-person-${p.entity_id}`}
    >
      {p.name}
    </button>
  );
}

export const MoveRow = memo(function MoveRow({ m, isMe, onTeamClick, onEntityClick, user }) {
  const { Icon, cls } = KIND[m.kind] || KIND.swap;
  const clickable = !!(onTeamClick && m.team_id);
  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${
        isMe ? 'bg-[hsl(var(--accent)/0.1)] border border-[hsl(var(--accent)/0.2)]' : ''
      } ${clickable ? 'cursor-pointer hover:bg-secondary/40 active:scale-[0.99] transition-all' : ''}`}
      onClick={clickable ? () => onTeamClick(m.team_id) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTeamClick(m.team_id); } } : undefined}
      data-testid={`transfer-activity-row-${m.id}`}
    >
      <img
        src={buildTeamRowAvatar(m, user, 48)}
        alt=""
        className="h-7 w-7 rounded-full bg-secondary object-cover shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className={`text-xs truncate leading-tight ${isMe ? 'font-semibold' : 'font-medium'}`}>
          {m.team_name || m.username || 'Unknown'}
          {m.username && <span className="text-muted-foreground font-normal"> @{m.username}</span>}
        </p>
        <p className="text-[11px] text-muted-foreground leading-tight truncate" data-testid={`transfer-activity-move-${m.id}`}>
          <Icon className={`inline h-3 w-3 mr-1 -mt-0.5 ${cls}`} />
          {m.dropped && <>dropped <Person p={m.dropped} onClick={onEntityClick} /></>}
          {m.kind === 'swap' && <span className="mx-1">→</span>}
          {m.added && <>added <Person p={m.added} onClick={onEntityClick} /></>}
          {m.memorial && <span className="ml-1.5 text-[9px] uppercase tracking-wide opacity-70">memorial</span>}
        </p>
      </div>
      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{timeAgo(m.created_at)}</span>
    </div>
  );
});

export default memo(function TransferActivity({ selectedLeagueId, onTeamClick, onEntityClick, onSeeAll }) {
  const { user } = useAuth();
  const [moves, setMoves] = useState(null);

  const load = useCallback(async () => {
    if (!selectedLeagueId) return;
    try {
      const res = await leagueAPI.transferActivity(selectedLeagueId, { limit: LIMIT });
      setMoves(res.data?.moves || []);
    } catch {
      setMoves((prev) => prev || []);
    }
  }, [selectedLeagueId]);

  useEffect(() => {
    setMoves(null);
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (!selectedLeagueId) return null;
  if (moves === null) return <Skeleton className="h-32 w-full rounded-2xl" data-testid="transfer-activity-loading" />;

  return (
    <div className="bg-card border border-border/60 rounded-2xl p-4" data-testid="transfer-activity">
      <div className="flex items-center gap-2 mb-3">
        <ArrowLeftRight className="h-4 w-4 text-[hsl(var(--accent))]" />
        <h3 className="text-sm font-semibold">Transfer Activity</h3>
        {moves.length > 0 && (
          <span className="ml-auto text-[10px] text-muted-foreground" data-testid="transfer-activity-count">
            last {moves.length} {moves.length === 1 ? 'move' : 'moves'}
          </span>
        )}
        {onSeeAll && moves.length > 0 && (
          <button
            type="button"
            onClick={onSeeAll}
            className="inline-flex items-center text-[11px] font-semibold text-[hsl(var(--accent))] hover:underline"
            data-testid="transfer-activity-see-all"
          >
            See all <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>

      {moves.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-3" data-testid="transfer-activity-empty">
          No roster moves yet — be the first to shake up the league.
        </p>
      ) : (
        <div className="space-y-1.5">
          {moves.map((m) => (
            <MoveRow
              key={m.id}
              m={m}
              isMe={m.user_id === user?.id}
              user={user}
              onTeamClick={onTeamClick}
              onEntityClick={onEntityClick}
            />
          ))}
        </div>
      )}
    </div>
  );
});
